import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { DropdownMulti, colors2, UI_BODY_SM_DF, DateRangePicker } from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";
import { useOAuthConnection } from "../../hooks/useOAuthConnection";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// ============================================
// SLACK ICON
// ============================================

export const SlackIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="60 60 150 150"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g>
      <g>
        <path fill="#E01E5A" d="M99.4,151.2c0,7.1-5.8,12.9-12.9,12.9c-7.1,0-12.9-5.8-12.9-12.9c0-7.1,5.8-12.9,12.9-12.9h12.9V151.2z"/>
        <path fill="#E01E5A" d="M105.9,151.2c0-7.1,5.8-12.9,12.9-12.9s12.9,5.8,12.9,12.9v32.3c0,7.1-5.8,12.9-12.9,12.9s-12.9-5.8-12.9-12.9V151.2z"/>
      </g>
      <g>
        <path fill="#36C5F0" d="M118.8,99.4c-7.1,0-12.9-5.8-12.9-12.9c0-7.1,5.8-12.9,12.9-12.9s12.9,5.8,12.9,12.9v12.9H118.8z"/>
        <path fill="#36C5F0" d="M118.8,105.9c7.1,0,12.9,5.8,12.9,12.9s-5.8,12.9-12.9,12.9H86.5c-7.1,0-12.9-5.8-12.9-12.9s5.8-12.9,12.9-12.9H118.8z"/>
      </g>
      <g>
        <path fill="#2EB67D" d="M170.6,118.8c0-7.1,5.8-12.9,12.9-12.9c7.1,0,12.9,5.8,12.9,12.9s-5.8,12.9-12.9,12.9h-12.9V118.8z"/>
        <path fill="#2EB67D" d="M164.1,118.8c0,7.1-5.8,12.9-12.9,12.9c-7.1,0-12.9-5.8-12.9-12.9V86.5c0-7.1,5.8-12.9,12.9-12.9c7.1,0,12.9,5.8,12.9,12.9V118.8z"/>
      </g>
      <g>
        <path fill="#ECB22E" d="M151.2,170.6c7.1,0,12.9,5.8,12.9,12.9c0,7.1-5.8,12.9-12.9,12.9c-7.1,0-12.9-5.8-12.9-12.9v-12.9H151.2z"/>
        <path fill="#ECB22E" d="M151.2,164.1c-7.1,0-12.9-5.8-12.9-12.9c0-7.1,5.8-12.9,12.9-12.9h32.3c7.1,0,12.9,5.8,12.9,12.9c0,7.1-5.8,12.9-12.9,12.9H151.2z"/>
      </g>
    </g>
  </svg>
);

const DateRangeRow = styled.div`
  margin-top: 16px;
`;

const DateRangeHint = styled(UI_BODY_SM_DF)`
  color: ${colors2.grey60};
  margin-top: 8px;
`;

const todayIso = () => new Date().toISOString().slice(0, 10);

export const SlackInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
}) => {
  const {
    connection,
    connectionLoading,
    connectError,
    preflightData,
    preflightError,
    isPreflightLoading,
    handleConnect,
    isConnecting,
    handleDisconnect,
  } = useOAuthConnection({ providerId: "slack", providerName: "Slack" });

  const [selectedChannels, setSelectedChannels] = useState([]);
  const [dateRange, setDateRange] = useState([todayIso(), todayIso()]);
  const [startDate, endDate] = dateRange;

  const submitErrorMessage =
    submitError && submitError.sourceId === "slack" ? submitError.message : null;
  const isEmptyResult =
    submitErrorMessage && /no slack messages found/i.test(submitErrorMessage);

  const channels = preflightData?.channels || [];
  const canSubmit = selectedChannels.length > 0 && startDate && endDate;

  const onDisconnect = async () => {
    await handleDisconnect();
    setSelectedChannels([]);
  };

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onClearSubmitError && onClearSubmitError();
    onSubmit({
      adapterOptions: {
        channelIds: selectedChannels.map((c) => c.value),
        oldest: moment(startDate).startOf("day").unix(),
        latest: moment(endDate).endOf("day").unix(),
      },
    });
  };

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Slack</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Slack</InterfaceHeader>
        <InterfaceDescription>
          Import messages from any Slack channel. You'll be redirected to Slack
          to authorize access, then brought back here to pick a channel and
          date range.
        </InterfaceDescription>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
        <InterfaceActions>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Connect to Slack</ButtonPrimary>
        </InterfaceActions>
        <HelpText>
          We request read access to channels, messages, and user info on behalf
          of your Slack account. Public channels are readable workspace-wide;
          private channels you're a member of are also included.
        </HelpText>
      </InterfaceContainer>
    );
  }

  if (connection.status === "stale") {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect Slack</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {connection.providerAccountName
                ? `Your Slack connection to ${connection.providerAccountName} expired or was revoked.`
                : "Your Slack connection expired or was revoked."}
            </span>
          </ConnectedMeta>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Reconnect Slack</ButtonPrimary>
        </StaleBanner>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      </InterfaceContainer>
    );
  }

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Slack</InterfaceHeader>
      <InterfaceDescription>
        Each Slack message becomes one row. Threads are flattened into a
        formatted transcript on the parent message.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected{connection.providerAccountName ? ` to ${connection.providerAccountName}` : ""}
          </strong>
          {connection.adminEmail && (
            <span style={{ color: colors2.grey60 }}>as {connection.adminEmail}</span>
          )}
          {connection.authorizedBy?.name && (
            <span style={{ color: colors2.grey60 }}>
              Authorized by: {connection.authorizedBy.name}
            </span>
          )}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isLoading}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="Slack"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      {preflightError && <ErrorBanner>{preflightError}</ErrorBanner>}
      {submitErrorMessage && isEmptyResult && <EmptyBanner>{submitErrorMessage}</EmptyBanner>}
      {submitErrorMessage && !isEmptyResult && <ErrorBanner>{submitErrorMessage}</ErrorBanner>}

      <div style={{ marginTop: 16 }}>
        <DropdownMulti
          label={adapterConfig?.preflightConfig?.selectLabel || "Select Channels"}
          placeholder={
            isPreflightLoading
              ? "Loading channels..."
              : adapterConfig?.preflightConfig?.selectPlaceholder || "Choose one or more channels to import..."
          }
          options={channels.map((ch) => ({
            display: `#${ch.name}${ch.isPrivate ? " (private)" : ""}`,
            value: ch.id,
            _channelInfo: ch,
          }))}
          value={selectedChannels}
          onChange={(k, v) => setSelectedChannels(v || [])}
          readOnly={isPreflightLoading || isLoading}
          isSearchable={true}
          searchbarPlaceholder="Search channels..."
          isTagsStyle
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </div>

      <DateRangeRow>
        <DateRangePicker
          id="slack-date-range"
          label="Date Range"
          value={dateRange}
          onChange={(k, v) => setDateRange(v)}
          previousDateOnly
          readOnly={isLoading}
        />
      </DateRangeRow>

      <DateRangeHint>
        Select a date range to limit the messages fetched. This helps avoid
        importing years of message history.
      </DateRangeHint>

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!canSubmit || isPreflightLoading || isLoading}
          isLoading={isLoading}
        >
          {adapterConfig?.preflightConfig?.submitLabel || "Import Messages"}
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

export const slackIntegrationMeta = {
  id: "slack",
  name: "Slack",
  description: "Import messages from Slack channels",
};

export default SlackInterface;
